import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useNotifications } from "@/lib/notifications";
import { PRESENCE_DOT } from "@/lib/format";
import {
  LayoutDashboard, Ticket, BookOpen, Users, Settings, LogOut,
  CircleDot, ChevronDown, Upload, Building2, Wand2, Megaphone, ScanLine,
  Bell, BellOff, ShieldCheck, ClipboardCheck, Activity, Send, BarChart3
} from "lucide-react";
import SystemSwitcher from "@/components/SystemSwitcher";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/tickets", label: "Tickets", icon: Ticket, testid: "nav-tickets" },
  { to: "/merchants", label: "Merchants", icon: Building2, testid: "nav-merchants" },
  { to: "/knowledge", label: "Knowledge Base", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/broadcast", label: "Broadcast", icon: Megaphone, testid: "nav-broadcast", perm: "broadcast.send" },
  { to: "/agent-reports", label: "Agent Reports", icon: Send, testid: "nav-agent-reports", perm: "agent_reports.manage" },
  { to: "/merchant-analytics", label: "Merchant Analytics", icon: BarChart3, testid: "nav-merchant-analytics", perm: "agent_reports.manage" },
  { to: "/healthcheck", label: "Health Check", icon: ScanLine, testid: "nav-healthcheck", perm: "health_check.view" },
  { to: "/monitoring", label: "Monitoring", icon: Activity, testid: "nav-monitoring" },
  { to: "/team", label: "Team Presence", icon: Users, testid: "nav-team" },
  { to: "/bulk-edit", label: "Bulk Edit", icon: Wand2, testid: "nav-bulk-edit", perm: "tickets.bulk_edit" },
  { to: "/import", label: "Bulk Import", icon: Upload, testid: "nav-import", perm: "import.run" },
  { to: "/bank-response", label: "Bank Response", icon: ClipboardCheck, testid: "nav-bank-response", perm: "import.run" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings", perm: "settings.view" },
  { to: "/settings/roles", label: "Roles & Permissions", icon: ShieldCheck, testid: "nav-roles", perm: "roles.manage" },
];

export default function AppLayout({ children }) {
  const { user, logout, setPresence } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-bg text-white">
      {/* Sidebar */}
      <aside className="w-60 border-r border-line bg-surface flex flex-col" data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-line">
          <div className="flex items-center gap-2.5">
            <img src="/assets/orbit-logo.svg" alt="ORBIT" className="w-9 h-9 object-contain" data-testid="orbit-logo-sidebar" />
            <div>
              <div className="font-bold text-sm tracking-tight">ORBIT</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">CS Console</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-2 space-y-0.5">
          {navItems.filter((it) => {
            if (!it.perm) return true;
            return (user?.permissions || []).includes(it.perm);
          }).map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-cyan/10 text-cyan border-l-2 border-cyan"
                    : "text-slate-300 hover:bg-surface2 hover:text-white border-l-2 border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="border-t border-line p-3" data-testid="sidebar-user">
          <div className="flex items-center gap-2.5 px-2 py-2">
            <div className="relative">
              <div className="w-8 h-8 rounded-sm bg-surface2 border border-line flex items-center justify-center text-xs font-bold text-cyan">
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-surface ${PRESENCE_DOT[user?.presence || "offline"]}`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.name}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">{user?.role}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1 mt-2">
            <button
              data-testid="presence-online-btn"
              onClick={() => setPresence("online")}
              className={`text-[10px] py-1 rounded-sm border uppercase tracking-wider transition-colors ${
                user?.presence === "online" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "border-line text-slate-400 hover:bg-surface2"
              }`}
            >
              ● Online
            </button>
            <button
              data-testid="presence-away-btn"
              onClick={() => setPresence("away")}
              className={`text-[10px] py-1 rounded-sm border uppercase tracking-wider transition-colors ${
                user?.presence === "away" ? "bg-amber-500/15 border-amber-500/40 text-amber-400" : "border-line text-slate-400 hover:bg-surface2"
              }`}
            >
              ● Away
            </button>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="mt-2 w-full flex items-center justify-center gap-2 text-xs py-1.5 border border-line rounded-sm text-slate-400 hover:bg-surface2 hover:text-rose-400 transition-colors"
          >
            <LogOut className="w-3 h-3" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto" data-testid="app-main">
        <div className="sticky top-0 z-30 bg-bg/95 backdrop-blur border-b border-line px-6 py-2.5 flex items-center justify-between">
          <SystemSwitcher />
          <div className="flex items-center gap-4">
            <NotificationsBell />
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">
              ORBIT v1.2 · Multi-System
            </div>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
}


function NotificationsBell() {
  const { notifiable, permission, requestPermission, recent, unread, markAllRead } = useNotifications();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!notifiable) return null;

  const denied = permission === "denied";
  const needAsk = permission === "default";

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell-btn"
        onClick={() => {
          if (!open && needAsk) requestPermission();
          setOpen((v) => !v);
          if (!open) markAllRead();
        }}
        className="relative p-1.5 rounded-sm border border-line hover:bg-surface2 transition-colors"
        title={denied ? "Notifikasi diblokir — aktifkan di pengaturan browser" : needAsk ? "Klik untuk izinkan notifikasi" : "Notifikasi"}
      >
        {denied ? <BellOff className="w-4 h-4 text-rose-400" /> : <Bell className="w-4 h-4 text-cyan" />}
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 top-full mt-2 w-[380px] bg-surface border border-line rounded-sm shadow-2xl z-50 animate-slide-up">
          <div className="px-3 py-2.5 border-b border-line flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-cyan font-semibold">Notifikasi</div>
            {needAsk && (
              <button
                data-testid="enable-notif-btn"
                onClick={async () => { await requestPermission(); }}
                className="text-[10px] text-cyan hover:underline"
              >
                Izinkan notifikasi desktop
              </button>
            )}
            {denied && (
              <span className="text-[10px] text-rose-400">Diblokir oleh browser</span>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {recent.length === 0 ? (
              <div className="px-3 py-8 text-center text-xs text-slate-500">Belum ada notifikasi.</div>
            ) : recent.map((n) => (
              <button
                key={n.id}
                onClick={() => { setOpen(false); if (n.link) nav(n.link); }}
                className="w-full text-left px-3 py-2.5 border-b border-line/40 hover:bg-surface2 transition-colors block"
                data-testid={`notif-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${n.type === "new_ticket" ? "bg-emerald-400" : "bg-amber-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{n.title}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-slate-500 mt-1 font-mono">{new Date(n.at).toLocaleTimeString()}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
